import { useState } from 'react';
import { useEnterprise, ROLES } from '../../contexts/EnterpriseContext';
import { toast } from 'sonner';
import { Users, ChevronDown, Check } from 'lucide-react';

const ROLE_OPTIONS = Object.entries(ROLES).map(([key, val]) => ({ value: key, label: val.label, color: val.color, desc: val.desc }));

const RevQTeamSettings = () => {
  const { teamMembers, providers, isEnterprise } = useEnterprise();
  const [team, setTeam] = useState(teamMembers);
  const [editingRole, setEditingRole] = useState(null);

  const updateRole = (userId, newRole) => {
    setTeam(prev => prev.map(m => m.id === userId ? { ...m, role: newRole } : m));
    setEditingRole(null);
    toast.success('Role updated');
  };

  const toggleStatus = (userId) => {
    setTeam(prev => prev.map(m => m.id === userId ? { ...m, status: m.status === 'active' ? 'inactive' : 'active' } : m));
    toast.success('Status updated');
  };

  if (!isEnterprise) {
    return (
      <div className="space-y-6" data-testid="revq-team-settings">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Team</h1>
          <p className="text-slate-500 mt-1">Enable Enterprise mode in Settings &rarr; Organization to manage team roles</p>
        </div>
        <div style={{ background: '#f9fafb', borderRadius: 12, padding: 40, textAlign: 'center' }}>
          <Users style={{ width: 32, height: 32, color: '#ccc', margin: '0 auto 12px' }} />
          <p style={{ fontSize: 14, color: '#888' }}>Enterprise mode is not active</p>
          <p style={{ fontSize: 12, color: '#aaa', marginTop: 4 }}>Go to Settings &rarr; Organization &rarr; Practice type to enable</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="revq-team-settings">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold font-['Plus_Jakarta_Sans'] text-slate-900">Team</h1>
          <p className="text-slate-500 mt-1">{team.filter(m => m.status === 'active').length} active members &middot; Enterprise mode</p>
        </div>
        <button style={{ height: 36, padding: '0 16px', background: '#111', color: '#fff', borderRadius: 8, fontSize: 13, fontWeight: 500, border: 'none', cursor: 'pointer' }}
          data-testid="add-team-member-btn"
          onClick={() => toast.info('Invite flow coming soon')}>
          + Add member
        </button>
      </div>

      {/* Team Table */}
      <div style={{ background: '#fff', border: '0.5px solid #e8e8e8', borderRadius: 12, overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '0.5px solid #f0f0f0' }}>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Member</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Role</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Assigned Providers</th>
              <th style={{ padding: '10px 16px', textAlign: 'left', fontSize: 10, fontWeight: 600, color: '#aaa', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status</th>
            </tr>
          </thead>
          <tbody>
            {team.map(member => (
              <tr key={member.id} style={{ borderBottom: '0.5px solid #f5f5f5' }} data-testid={`team-member-${member.id}`}>
                <td style={{ padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{ width: 32, height: 32, borderRadius: '50%', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, color: '#555' }}>
                      {member.avatar}
                    </div>
                    <div>
                      <p style={{ fontSize: 13, fontWeight: 500, color: '#111', margin: 0 }}>{member.name}</p>
                      <p style={{ fontSize: 11, color: '#aaa', margin: 0 }}>{member.email}</p>
                    </div>
                  </div>
                </td>
                <td style={{ padding: '12px 16px', position: 'relative' }}>
                  <div style={{ position: 'relative' }}>
                    <button
                      onClick={() => setEditingRole(editingRole === member.id ? null : member.id)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 6, padding: '4px 10px',
                        border: '0.5px solid #e0e0e0', borderRadius: 6, background: '#fff', cursor: 'pointer', fontSize: 12,
                      }}
                      data-testid={`role-select-${member.id}`}>
                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: ROLES[member.role]?.color || '#888' }} />
                      {ROLES[member.role]?.label || member.role}
                      <ChevronDown style={{ width: 10, height: 10, color: '#aaa' }} />
                    </button>
                    {editingRole === member.id && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, marginTop: 4, width: 240, background: '#fff',
                        border: '1px solid #e0e0e0', borderRadius: 8, boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 20,
                      }}>
                        {ROLE_OPTIONS.map(opt => (
                          <button key={opt.value}
                            onClick={() => updateRole(member.id, opt.value)}
                            style={{
                              display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px',
                              border: 'none', background: member.role === opt.value ? '#f5f5f5' : '#fff', cursor: 'pointer', textAlign: 'left',
                            }}>
                            <span style={{ width: 6, height: 6, borderRadius: '50%', background: opt.color }} />
                            <div style={{ flex: 1 }}>
                              <p style={{ fontSize: 12, fontWeight: 500, color: '#111', margin: 0 }}>{opt.label}</p>
                              <p style={{ fontSize: 10, color: '#aaa', margin: 0 }}>{opt.desc}</p>
                            </div>
                            {member.role === opt.value && <Check style={{ width: 12, height: 12, color: '#22c55e' }} />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </td>
                <td style={{ padding: '12px 16px' }}>
                  {(member.role === 'supervisor' || member.role === 'admin') ? (
                    <span style={{ fontSize: 11, color: '#aaa', fontStyle: 'italic' }}>All providers</span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                      {member.providers.length === 0 ? (
                        <span style={{ fontSize: 11, color: '#f59e0b' }}>Unassigned</span>
                      ) : member.providers.map(p => (
                        <span key={p} style={{ fontSize: 10, padding: '2px 8px', background: '#f5f5f5', borderRadius: 4, color: '#555' }}>{p}</span>
                      ))}
                    </div>
                  )}
                </td>
                <td style={{ padding: '12px 16px' }}>
                  <button onClick={() => toggleStatus(member.id)}
                    style={{
                      fontSize: 11, padding: '3px 10px', borderRadius: 20, border: 'none', cursor: 'pointer',
                      background: member.status === 'active' ? '#f0fdf4' : '#f5f5f5',
                      color: member.status === 'active' ? '#16a34a' : '#aaa',
                      fontWeight: 500,
                    }}
                    data-testid={`toggle-status-${member.id}`}>
                    {member.status === 'active' ? 'Active' : 'Inactive'}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default RevQTeamSettings;
